import { useEffect, useRef, useState } from 'react';
import {
  findSelectPortalMenus,
  findDatepickerPortal,
  findMultiSelectDropdowns,
  findMultiFilterPortal,
} from '../utils/selectPortal';

const getScrollableAncestors = (element: Element): Element[] => {
  const ancestors: Element[] = [];
  let current = element.parentElement;
  while (current) {
    const style = window.getComputedStyle(current);
    const overflowX = style.overflowX;
    const overflowY = style.overflowY;
    if (overflowX === 'auto' || overflowX === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
      ancestors.push(current);
    }
    current = current.parentElement;
  }
  return ancestors;
};

const isElementVisible = (element: Element): boolean => {
  let current: Element | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    current = current.parentElement;
  }
  return true;
};

const buildRect = (elements: Element[]): DOMRect | null => {
  const visible = elements.filter(isElementVisible);
  if (visible.length === 0) return null;
  const rects = visible.map((el) => el.getBoundingClientRect());
  const combined = rects.reduce(
    (acc, r) => ({
      top: Math.min(acc.top, r.top),
      left: Math.min(acc.left, r.left),
      bottom: Math.max(acc.bottom, r.bottom),
      right: Math.max(acc.right, r.right),
    }),
    { top: Infinity, left: Infinity, bottom: -Infinity, right: -Infinity },
  );
  return new DOMRect(combined.left, combined.top, combined.right - combined.left, combined.bottom - combined.top);
};

export const useTargetPosition = (uiTarget: string, isActive: boolean) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [portalRect, setPortalRect] = useState<DOMRect | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const scrollCleanupRef = useRef<(() => void)[]>([]);

  useEffect(() => {
    setTargetRect(null);
    setPortalRect(null);

    if (!uiTarget || !isActive) {
      return;
    }

    const cleanupScrollListeners = () => {
      scrollCleanupRef.current.forEach((fn) => fn());
      scrollCleanupRef.current = [];
    };

    const updateTargetPosition = () => {
      const targets = uiTarget.split('|').map((t) => t.trim());
      if (targets.length === 0) return;

      const mainElements: Element[] = [];
      const portalElements: Element[] = [];

      for (const target of targets) {
        const selector = `[data-walkthrough="${target}"]`;
        const foundElements = document.querySelectorAll(selector);
        foundElements.forEach((el) => mainElements.push(el));

        foundElements.forEach((el) => {
          const menus = findSelectPortalMenus(el);
          menus.forEach((menu) => {
            if (!portalElements.includes(menu)) portalElements.push(menu);
          });

          const datepicker = findDatepickerPortal(el);
          if (datepicker && !portalElements.includes(datepicker)) {
            portalElements.push(datepicker);
          }

          const multiSelectDropdowns = findMultiSelectDropdowns(el);
          multiSelectDropdowns.forEach((dropdown) => {
            if (!portalElements.includes(dropdown)) portalElements.push(dropdown);
          });

          const multiFilterPortal = findMultiFilterPortal(el);
          if (multiFilterPortal && !portalElements.includes(multiFilterPortal)) {
            portalElements.push(multiFilterPortal);
          }
        });
      }

      if (mainElements.length === 0) {
        setTargetRect(null);
        setPortalRect(null);
        return;
      }

      setTargetRect(buildRect(mainElements));
      setPortalRect(portalElements.length > 0 ? buildRect(portalElements) : null);
    };

    const scrollIntoViewAndSetup = () => {
      const targets = uiTarget.split('|').map((t) => t.trim());
      const firstElement = targets.reduce<Element | null>((found, target) => {
        if (found) return found;
        return document.querySelector(`[data-walkthrough="${target}"]`);
      }, null);

      if (firstElement) {
        firstElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

        cleanupScrollListeners();
        const scrollableAncestors = getScrollableAncestors(firstElement);
        scrollableAncestors.forEach((ancestor) => {
          const handler = () => updateTargetPosition();
          ancestor.addEventListener('scroll', handler, { passive: true });
          scrollCleanupRef.current.push(() => ancestor.removeEventListener('scroll', handler));
        });
      }

      setTimeout(updateTargetPosition, 350);
    };

    scrollIntoViewAndSetup();

    observerRef.current = new MutationObserver(updateTargetPosition);
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    window.addEventListener('resize', updateTargetPosition);
    window.addEventListener('scroll', updateTargetPosition);

    return () => {
      observerRef.current?.disconnect();
      cleanupScrollListeners();
      window.removeEventListener('resize', updateTargetPosition);
      window.removeEventListener('scroll', updateTargetPosition);
    };
  }, [uiTarget, isActive]);

  return { targetRect, portalRect };
};
